<template>
  <a-card id="weight-and-shipping">
    <h4 class="mb-4">
      {{ $t('product.weight_product_shipping') }}
    </h4>
    <a-form-model-item>
      <a-checkbox v-model="isNonFisik" :disabled="isLoading" @change="changePhysical">
        {{ $t('product.non_physical_product') }}
      </a-checkbox>
    </a-form-model-item>
    <a-collapse v-if="!isNonFisik" v-model="activeKey" expand-icon-position="right" class="unit-collapse">
      <a-collapse-panel
        v-for="(val, index) in units"
        :key="val.unit"
        :header="val.unit"
        :class="{ 'first-unit': index == 0 }"
      >
        <a-row :gutter="16">
          <template v-if="index == 0">
            <a-col :span="8">
              <a-form-model-item :label="$t('product.base_unit')" :prop="'units.' + index + '.unit'">
                <a-select v-model="val.unit" size="large" class="select-antd-default" disabled>
                  <a-select-option v-for="unit in unusedUnits" :key="unit" :value="unit">
                    {{ unit }}
                  </a-select-option>
                </a-select>
              </a-form-model-item>
            </a-col>
          </template>
          <template v-else>
            <a-col :span="7">
              <a-form-model-item
                :label="$t('product.base_unit')"
                :prop="'units.' + index + '.numerator'"
                :rules="[{ validator: validateUnit, trigger: 'blur' }]"
              >
                <a-input v-model="val.numerator" size="large" :disabled="isLoading" @keypress="numberOnly">
                  <div slot="addonAfter" style="width: 60px">
                    {{ units[0].unit }}
                  </div>
                </a-input>
              </a-form-model-item>
            </a-col>
            <a-col :span="2" class="text-center" style="padding-top: 50px">
              =
            </a-col>
            <a-col :span="7">
              <a-form-model-item
                :label="$t('product.quantity_conversion')"
                :prop="'units.' + index + '.denumerator'"
                :rules="[{ validator: validateUnit, trigger: 'blur' }]"
              >
                <a-input v-model="val.denumerator" size="large" :disabled="isLoading" @keypress="numberOnly">
                  <a-select slot="addonAfter" v-model="val.unit" class="w-90px" :disabled="isLoading">
                    <div slot="dropdownRender" slot-scope="menu">
                      <v-nodes :vnodes="menu" />
                      <a-divider style="margin: 2px 0;" />
                      <div class="btn-uom" @mousedown="e => e.preventDefault()" @click="$emit('addUom')">
                        {{ $t('product.add_new_unit') }}
                      </div>
                    </div>
                    <a-select-option v-for="unit in unusedUnits" :key="unit" :value="unit">
                      {{ unit }}
                    </a-select-option>
                  </a-select>
                </a-input>
              </a-form-model-item>
            </a-col>
          </template>
          <a-col :span="8">
            <a-form-model-item
              :label="$t('product.product_weight_per_unit', { unit: val.unit || '-' })"
              :prop="'units.' + index + '.weight'"
              :rules="[
                { required: true, message: $t('product.validate_unit_required'), trigger: 'blur' },
                { validator: validateUnit, trigger: 'blur' }
              ]"
            >
              <a-input v-model="val.weight" size="large" :disabled="isLoading" @keypress="numberOnly">
                <a-select
                  slot="addonAfter"
                  v-model="val.weight_unit"
                  :options="weightSizes"
                  class="w-90px"
                  :disabled="isLoading"
                />
              </a-input>
            </a-form-model-item>
          </a-col>
        </a-row>
        <a-row :gutter="16">
          <a-col :span="8">
            <a-form-model-item
              :label="$t('product.length')"
              :prop="'units.' + index + '.length'"
              :rules="[
                { required: true, message: $t('product.validate_unit_required'), trigger: 'blur' },
                { validator: validateUnit, trigger: 'blur' }
              ]"
            >
              <a-input v-model="val.length" size="large" :disabled="isLoading" @keypress="numberOnly">
                <a-select
                  slot="addonAfter"
                  v-model="val.length_unit"
                  :options="packageSizes"
                  class="w-90px"
                  :disabled="isLoading"
                />
              </a-input>
            </a-form-model-item>
          </a-col>
          <a-col :span="8">
            <a-form-model-item
              :label="$t('product.width')"
              :prop="'units.' + index + '.width'"
              :rules="[
                { required: true, message: $t('product.validate_unit_required'), trigger: 'blur' },
                { validator: validateUnit, trigger: 'blur' }
              ]"
            >
              <a-input v-model="val.width" size="large" :disabled="isLoading" @keypress="numberOnly">
                <a-select
                  slot="addonAfter"
                  v-model="val.width_unit"
                  :options="packageSizes"
                  class="w-90px"
                  :disabled="isLoading"
                />
              </a-input>
            </a-form-model-item>
          </a-col>
          <a-col :span="8">
            <a-form-model-item
              :label="$t('product.height')"
              :prop="'units.' + index + '.height'"
              :rules="[
                { required: true, message: $t('product.validate_unit_required'), trigger: 'blur' },
                { validator: validateUnit, trigger: 'blur' }
              ]"
            >
              <a-input v-model="val.height" size="large" :disabled="isLoading" @keypress="numberOnly">
                <a-select
                  slot="addonAfter"
                  v-model="val.height_unit"
                  :options="packageSizes"
                  class="w-90px"
                  :disabled="isLoading"
                />
              </a-input>
            </a-form-model-item>
          </a-col>
        </a-row>
        
        <a-popconfirm
          v-if="index > 0"
          slot="extra" 
          :title="$t('product.confirm_remove_unit', { unit: val.unit || '' })" 
          @confirm="removeUnit(index)"
        >
          <a-button
            v-if="!isLoading"
            type="primary"
            size="small"
            ghost
            icon="delete"
            @click.stop=""
          >
            {{ $t('utils.delete') }}
          </a-button>
        </a-popconfirm>
      </a-collapse-panel>
    </a-collapse>

    <a-button
      v-if="!isNonFisik"
      ghost
      type="primary"
      icon="plus"
      class="mt-4"
      :disabled="isLoading"
      @click="addUnit"
    >
      {{ $t('product.add_new_unit') }}
    </a-button>
  </a-card>
</template>

<script>
import { useInputNumber } from '@/composable/useInputNumber'

export default {
  name: 'WeightAndShipping',
  components: {
    VNodes: {
      functional: true,
      render: (h, ctx) => ctx.props.vnodes,
    },
  },
  props: {
    value: {
      type: Array,
      default: () => [],
    },
    loading: {
      type: Boolean,
      default: false,
    },
    uom: {
      type: Array,
      default: () => [],
    },
  },
  setup() {
    const { numberOnly } = useInputNumber()
    return { numberOnly }
  },
  data() {
    return {
      isNonFisik: false,
      defaultValue: {
        unit: '',
        denumerator: 1,
        numerator: 1,
        height_unit: 'cm',
        width_unit: 'cm',
        length_unit: 'cm',
        height: null,
        width: null,
        length: null,
        weight: null,
        weight_unit: 'g',
      },
      activeKey: ['PCS', 'DZN', 'CTN'],
      weightSizes: [
        { value: 'g', label: 'Gram' },
        { value: 'kg', label: 'Kg' },
      ],
      packageSizes: [
        { value: 'mm', label: 'MM' },
        { value: 'cm', label: 'CM' },
        { value: 'm', label: 'M' },
      ],
    }
  },
  computed: {
    units: {
      get() {
        return this.value
      },
      set(newValue) {
        this.$emit('input', newValue)
      },
    },
    isLoading() {
      return this.loading
    },
    uomList() {
      return this.uom
    },
    selectedUnit() {
      return [ ...new Set(this.units.map(u => u.unit)) ]
    },
    unusedUnits() {
      return this.uomList.filter(unit => !this.selectedUnit.includes(unit))
    },
  },
  methods: {
    validateUnit(rule, value, callback) {
      const fieldName = rule.field.split('.')[2]
      if (value === '') {
        callback(new Error(this.$t('product.validate_unit_required')))
      } else if (+value == 0) {
        callback(new Error(this.$t('product.validate_unit_min')))
      } else if (fieldName === 'numerator' || fieldName === 'denumerator') {
        const index = rule.field.split('.')[1]
        const numeratorValue = fieldName === 'numerator'
          ? value
          : this.units[index].numerator
        const denumeratorValue = fieldName === 'denumerator'
          ? value
          : this.units[index].denumerator
        
        if (+numeratorValue < +denumeratorValue) {
          callback(new Error(this.$t('product.validate_unit_conversion')))
        } else {
          const validateField = fieldName === 'numerator'
            ? 'denumerator'
            : 'numerator'
          this.$emit('validate', `units.${index}.${validateField}`)
          callback()
        }
      } else {
        callback()
      }
    },
    changePhysical(event) {
      this.$emit('changePhysical', !event.target.checked)
    },
    addUnit() {
      if (!this.unusedUnits.length) return

      const units = this.copyUnits()
      units.push({
        ...this.defaultValue,
        unit: this.unusedUnits[0],
      })
      this.$emit('input', units)
    },
    removeUnit(index) {
      const units = this.copyUnits()
      units.splice(index, 1)
      this.$emit('input', units)
    },
    copyUnits() {
      return JSON.parse(JSON.stringify(this.units))
    },
  },
}
</script>

<style lang="scss">
.w-90px {
  width: 90px !important;
}

.select-antd-default.ant-select-lg .ant-select-selection__rendered {
  line-height: 38px !important;
}

.unit-collapse {
  background-color: white !important;
  border: none !important;

  .ant-collapse-item {
    margin-bottom: 1rem !important;

    &:last-child {
      margin-bottom: 0 !important;
    }
  }

  .ant-collapse-header {
    background-color: #fafafa;
    border-top: 1px solid #e4e9f0;
  }

  .ant-collapse-header,
  .ant-collapse-content-box {
    border-right: 1px solid #e4e9f0;
    border-left: 1px solid #e4e9f0;
  }
}

.first-unit {
  border: none !important;
  
  .ant-collapse-header {
    display: none;
  }

  .ant-collapse-content {
    border: none;
  }

  .ant-collapse-content-box {
    padding: 0 !important;
    border: none;
  }
}
</style>